import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Icon,
  ModalWithTitle,
  TextField,
  Typography,
  useTheme,
} from "@outschool/backpack";
import { ActivitySubject } from "@outschool/business-rules";
import {
  IconDefinition,
  faCalendar,
  faHeart,
  faSchool,
  faUser,
} from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { PickLearnerAvatarModal } from "@outschool/ui-components-classroom";
import {
  AvatarImage,
  DEFAULT_AVATAR,
  ErrorMessage,
} from "@outschool/ui-components-shared";
import React, { useRef } from "react";

import { TIMES_OF_DAY } from "../../AvailableTimesInput/utils";
import { DateOfBirthPicker } from "../../learner/DateOfBirthPicker";
import {
  LEARNER_NAME_MAX_LENGTH,
  LearnerNameWarning,
} from "../../learner/LearnerNameWarning";
import { AttributeCheckboxList } from "../../loggedInHome/learnerCards/AttributeCheckboxList";
import AvailableTimesInput from "../../loggedInHome/learnerCards/AvailableTimesInput";
import { SchoolingApproachButtons } from "../../loggedInHome/learnerCards/SchoolingApproachButtons";
import { getLabelFromValue } from "../../loggedInHome/learnerCards/utils";
import { MultiSelectSubjects } from "../../onboarding/components/MultiSelectSubjects";
import {
  CLASS_SIZE_SELECTIONS,
  LEARNING_FORMAT_SELECTIONS,
  LEARNING_NEEDS_SELECTIONS,
  LearningNeedsType,
} from "../../onboarding/consts";
import { findSelectedTimeOptionFromAvailableTimes } from "../../onboarding/utils";
import { useUpsertLearner } from "../hooks/useUpsertLearner";

const AccordionPanels = {
  schoolingApproach: "schoolingApproach",
  interests: "interests",
  setup: "setup",
  availability: "availability",
  needs: "needs",
};

function StyledAccordionSummary({
  text,
  icon,
  isExpanded,
}: {
  text: string;
  icon: IconDefinition;
  isExpanded: boolean;
}) {
  const theme = useTheme();

  return (
    <AccordionSummary
      sx={{
        paddingX: "0.5em",
        borderRadius: "0.5em",
        backgroundColor: isExpanded ? "primary.50" : "inherit",
        border: isExpanded
          ? `1px solid ${theme.palette.primary[50]}`
          : `1px solid ${theme.palette.grey[300]}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Icon
          icon={icon}
          sx={{
            fontSize: "1.5em",
            color: "primary.main",
            marginRight: "0.25em",
          }}
        />
        <Typography
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "240px",
            [theme.breakpoints.up("sm")]: {
              width: "450px",
            },
          }}
        >
          {text}
        </Typography>
      </Box>
    </AccordionSummary>
  );
}

export default function AddLearnerModal(
  props: Omit<ReturnType<typeof useUpsertLearner>, "openModal">,
) {
  const { t } = useTranslation(
    "client\\components\\parentDashboardV2\\components\\AddLearnerModal",
  );
  const availabilityRef = useRef<HTMLElement | null>(null);
  const theme = useTheme();

  const {
    uiState,
    childState,
    preferencesState,
    updateUiState,
    updateChildState,
    updatePreferencesState,
    handleChangeAccordion,
    upsertChild,
    schoolingApproachText,
    isLoading,
    closeModal,
  } = props;

  // Destructure grouped state for convenience
  const { isModalOpen, showPickAvatarModal, expandedAccordion, error } =
    uiState;
  const { child, childNameHasChanged } = childState;
  const {
    childSubjects,
    schoolingApproach,
    childNeeds,
    childFormats,
    childClassSizes,
  } = preferencesState;

  return (
    <>
      <ModalWithTitle
        closeButtonArialLabel={t("close add learner form")}
        headerTitle={
          <Typography variant="h6" sx={{ color: theme.palette.neutral[900] }}>
            {t("Add your learner to get the best classes")}
          </Typography>
        }
        open={isModalOpen}
        onClose={closeModal}
        sx={{
          maxWidth: 700,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
        }}
        modalHeaderProps={{
          sx: {
            // eslint-disable-next-line i18next/no-literal-string
            borderBottom: "none",
            color: theme.palette.neutral[700],
            paddingBottom: 0,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            px: "1.6rem",
            pb: "1.6rem",
            // account for header height and place scroll on inner container
            maxHeight: "calc(100% - 50px)",
          }}
        >
          <ErrorMessage value={error} />
          <Box
            sx={{
              marginTop: "0.5em",
              padding: "1em",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "1em",
              border: `1px solid ${theme.palette.primary[50]}`,
              borderRadius: "0.5em",
              boxShadow: "0px 1px 8px 0px #0000000A",
              overflowY: "auto",
            }}
          >
            {/* avatar */}
            <Box
              flex
              sx={{
                margin: "auto",
                flexDirection: "column",
                position: "relative",
                width: 80,
                height: 80,
              }}
            >
              <AvatarImage
                avatar={child.avatar || DEFAULT_AVATAR}
                height={80}
                width={80}
                sx={{ alignSelf: "center" }}
              />
              <Button
                sx={{
                  backgroundColor: `${theme.palette.common.white} !important`,
                  color: theme.palette.neutral[600],
                  fontSize: "0.75em",
                  opacity: "0.74",
                  position: "absolute",
                  top: 25,
                  left: 17,
                  width: 46,
                  height: 28,
                }}
                variant="text"
                onClick={() => updateUiState({ showPickAvatarModal: true })}
                size="small"
              >
                {t("EDIT")}
              </Button>
            </Box>
            {/* name and age */}
            <Box>
              <Box
                flex
                sx={{
                  gap: "0.5em",
                  [theme.breakpoints.down("sm")]: {
                    flexWrap: "wrap",
                    gap: "1em",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "1.2rem", color: "grey.500" }}
                  >
                    {t("FIRST NAME")}
                  </Typography>
                  <TextField
                    autoComplete="off"
                    size="small"
                    placeholder={t("First name")}
                    value={child.name || ""}
                    onChange={e => {
                      updateChildState({
                        child: { ...child, name: e.target.value },
                        childNameHasChanged: true,
                      });
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    [theme.breakpoints.down("sm")]: {
                      minWidth: 250,
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "1.2rem", color: "grey.500" }}
                  >
                    {t("BIRTHDAY")}
                  </Typography>
                  <DateOfBirthPicker
                    birthMonth={child.birthMonth}
                    birthYear={child.birthYear}
                    onChange={(birthMonth: number, birthYear: number) => {
                      updateChildState({
                        child: { ...child, birthMonth, birthYear },
                      });
                    }}
                    selectSx={{ width: "100%", marginBottom: 0 }}
                    highlightErrors={false}
                  />
                </Box>
              </Box>
              {childNameHasChanged && (
                <LearnerNameWarning name={child.name || ""} />
              )}
            </Box>
            {/* Schooling approach */}
            <Box>
              <Typography
                variant="body2"
                sx={{ fontSize: "1.2rem", color: "grey.500" }}
              >
                {t("SCHOOLING APPROACH")}
              </Typography>
              <Accordion
                expanded={
                  expandedAccordion === AccordionPanels.schoolingApproach
                }
                onChange={(_event: React.SyntheticEvent, isExpanded: boolean) =>
                  handleChangeAccordion(
                    AccordionPanels.schoolingApproach,
                    isExpanded,
                  )
                }
              >
                <StyledAccordionSummary
                  icon={faSchool}
                  text={schoolingApproachText}
                  isExpanded={
                    expandedAccordion === AccordionPanels.schoolingApproach
                  }
                />
                <AccordionDetails sx={{ marginTop: "1em" }}>
                  <SchoolingApproachButtons
                    handleChangeApproach={approach => {
                      updatePreferencesState({ schoolingApproach: approach });
                    }}
                    selectedSchoolingApproach={schoolingApproach}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* Interests */}
            <Box>
              <Typography
                variant="body2"
                sx={{ fontSize: "1.2rem", color: "grey.500" }}
              >
                {t("INTERESTS")}
              </Typography>
              <Accordion
                expanded={expandedAccordion === AccordionPanels.interests}
                onChange={(_event: React.SyntheticEvent, isExpanded: boolean) =>
                  handleChangeAccordion(AccordionPanels.interests, isExpanded)
                }
              >
                <StyledAccordionSummary
                  icon={faHeart}
                  text={
                    childSubjects.length
                      ? childSubjects.join(", ")
                      : t`Select interests`
                  }
                  isExpanded={expandedAccordion === AccordionPanels.interests}
                />
                <AccordionDetails sx={{ marginTop: "1em" }}>
                  <MultiSelectSubjects
                    selectedSubjects={childSubjects as ActivitySubject[]}
                    setSelectedSubjects={subjects =>
                      updatePreferencesState({ childSubjects: subjects })
                    }
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
            {/* Class setup */}
            <Box>
              <Typography
                variant="body2"
                sx={{ fontSize: "1.2rem", color: "grey.500" }}
              >
                {t("CLASS SETUP")}
              </Typography>
              <Accordion
                expanded={expandedAccordion === AccordionPanels.setup}
                onChange={(_event: React.SyntheticEvent, isExpanded: boolean) =>
                  handleChangeAccordion(AccordionPanels.setup, isExpanded)
                }
              >
                <StyledAccordionSummary
                  icon={faHeart}
                  text={
                    childFormats.length || childClassSizes.length
                      ? childFormats
                          .map(format =>
                            getLabelFromValue({
                              value: format,
                              selections: LEARNING_FORMAT_SELECTIONS,
                            }),
                          )
                          .concat(
                            childClassSizes.map(size =>
                              getLabelFromValue({
                                value: size,
                                selections: CLASS_SIZE_SELECTIONS,
                              }),
                            ),
                          )
                          .join(", ")
                      : t`Select class preferences`
                  }
                  isExpanded={expandedAccordion === AccordionPanels.setup}
                />
                <AccordionDetails sx={{ marginTop: "1em" }}>
                  <Box
                    sx={{
                      marginTop: "1.25em",
                      marginLeft: "0.5em",
                      display: "flex",
                      [theme.breakpoints.down("sm")]: {
                        flexDirection: "column",
                        gap: "1em",
                      },
                    }}
                  >
                    <Box sx={{ marginRight: "1em", flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ marginBottom: "0.25em" }}
                      >{t`Class format`}</Typography>
                      <AttributeCheckboxList
                        selections={LEARNING_FORMAT_SELECTIONS}
                        selectedValues={childFormats}
                        setValues={formats =>
                          updatePreferencesState({ childFormats: formats })
                        }
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ marginBottom: "0.25em" }}
                      >{t`Class size`}</Typography>
                      <AttributeCheckboxList
                        selections={CLASS_SIZE_SELECTIONS}
                        selectedValues={childClassSizes}
                        setValues={sizes =>
                          updatePreferencesState({ childClassSizes: sizes })
                        }
                      />
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
            {/* Schedule */}
            <Box>
              <Typography
                variant="body2"
                sx={{ fontSize: "1.2rem", color: "grey.500" }}
              >
                {t("SCHEDULE")}
              </Typography>
              <Accordion
                expanded={expandedAccordion === AccordionPanels.availability}
                onChange={(_event: React.SyntheticEvent, isExpanded: boolean) =>
                  handleChangeAccordion(
                    AccordionPanels.availability,
                    isExpanded,
                  )
                }
              >
                <StyledAccordionSummary
                  icon={faCalendar}
                  text={
                    !child.availableTimes || child.availableTimes.length === 0
                      ? t("Set schedule")
                      : findSelectedTimeOptionFromAvailableTimes(
                          TIMES_OF_DAY,
                          child.availableTimes,
                        )?.label || "Custom"
                  }
                  isExpanded={
                    expandedAccordion === AccordionPanels.availability
                  }
                />
                <AccordionDetails sx={{ marginTop: "1em" }}>
                  <AvailableTimesInput
                    availableTimes={child.availableTimes || []}
                    onUpdateAll={times => {
                      updateChildState({
                        child: { ...child, availableTimes: times || [] },
                      });
                      updateUiState({ error: undefined });
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* Learning Needs */}
            <Box>
              <Typography
                variant="body2"
                sx={{ fontSize: "1.2rem", color: "grey.500" }}
              >
                {t("LEARNING NEEDS")}
              </Typography>
              <Accordion
                expanded={expandedAccordion === AccordionPanels.needs}
                onChange={(_event: React.SyntheticEvent, isExpanded: boolean) =>
                  handleChangeAccordion(AccordionPanels.needs, isExpanded)
                }
              >
                <StyledAccordionSummary
                  icon={faUser}
                  text={
                    childNeeds.length
                      ? childNeeds
                          .map(need =>
                            getLabelFromValue({
                              value: need,
                              selections: LEARNING_NEEDS_SELECTIONS,
                            }),
                          )
                          .join(", ")
                      : t`Select needs`
                  }
                  isExpanded={expandedAccordion === AccordionPanels.needs}
                />
                <AccordionDetails
                  sx={{ marginTop: "1em", marginLeft: "0.5em" }}
                >
                  <AttributeCheckboxList
                    selections={LEARNING_NEEDS_SELECTIONS}
                    selectedValues={childNeeds}
                    setValues={needs =>
                      updatePreferencesState({ childNeeds: needs })
                    }
                    specialNoneValue={LearningNeedsType.None}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>

          <Box
            sx={{
              marginTop: "1.5em",
              marginBottom: "0.5em",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              sx={{ width: "75%" }}
              onClick={upsertChild}
              disabled={
                !!error ||
                isLoading ||
                !child.name ||
                child.name.length > LEARNER_NAME_MAX_LENGTH ||
                !child.birthYear ||
                !child.birthMonth
              }
              ref={availabilityRef}
            >
              {t("Save")}
            </Button>
          </Box>
        </Box>
      </ModalWithTitle>

      {showPickAvatarModal && (
        <PickLearnerAvatarModal
          onSelect={avatar => {
            updateUiState({ showPickAvatarModal: false });
            updateChildState({ child: { ...child, avatar: avatar } });
          }}
          onCancel={() => updateUiState({ showPickAvatarModal: false })}
        />
      )}
    </>
  );
}
